import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  TablePagination,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  Receipt,
  CalendarToday,
  Category,
  Numbers,
  Settings,
  Visibility
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInvoiceNumberMasters,
  fetchCurrentFYInvoiceNumberMasters,
  createInvoiceNumberMaster,
  updateInvoiceNumberMaster,
  deleteInvoiceNumberMaster,
  generateNextInvoiceNumber,
  getCurrentFinancialYear,
  clearInvoiceNumberMasterError
} from '../redux/actions/invoiceNumberMasterActions';

const InvoiceNumberMaster = () => {
  const dispatch = useDispatch();
  const {
    masters,
    currentFYMaster,
    mastersLoading,
    createLoading,
    updateLoading,
    deleteLoading,
    generateLoading,
    mastersError,
    createError,
    updateError,
    deleteError,
    generateError,
    pagination,
    currentFinancialYear,
    generatedInvoiceNumber
  } = useSelector(state => state.invoiceNumberMaster);

  // State
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    financial_year: '',
    prefix: '',
    current_sequence: 1,
    max_sequence: 999999,
    format: '',
    is_active: true
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchInvoiceNumberMasters({ page: 1, limit: rowsPerPage }));
    dispatch(fetchCurrentFYInvoiceNumberMasters());
    dispatch(getCurrentFinancialYear());
  }, [dispatch, rowsPerPage]);

  // Set current financial year when available
  useEffect(() => {
    if (currentFinancialYear && !formData.financial_year) {
      setFormData(prev => ({ ...prev, financial_year: currentFinancialYear }));
    }
  }, [currentFinancialYear, formData.financial_year]);

  // Handle dialog open
  const handleOpenDialog = (mode, master = null) => {
    setDialogMode(mode);
    setSelectedMaster(master);
    
    if (mode === 'edit' && master) {
      setFormData({
        category: master.category,
        financial_year: master.financial_year,
        prefix: master.prefix,
        current_sequence: master.current_sequence,
        max_sequence: master.max_sequence,
        format: master.format,
        is_active: master.is_active
      });
    } else if (mode === 'view' && master) {
      setFormData({
        category: master.category,
        financial_year: master.financial_year,
        prefix: master.prefix,
        current_sequence: master.current_sequence,
        max_sequence: master.max_sequence,
        format: master.format,
        is_active: master.is_active
      });
    } else {
      setFormData({
        category: '',
        financial_year: currentFinancialYear || '',
        prefix: '',
        current_sequence: 1,
        max_sequence: 999999,
        format: '',
        is_active: true
      });
    }
    
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMaster(null);
    setFormData({
      category: '',
      financial_year: currentFinancialYear || '',
      prefix: '',
      current_sequence: 1,
      max_sequence: 999999,
      format: '',
      is_active: true
    });
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate format when prefix, financial_year changes
      if (field === 'prefix' || field === 'financial_year') {
        const prefix = field === 'prefix' ? value : newData.prefix;
        const fy = field === 'financial_year' ? value : newData.financial_year;
        if (prefix && fy) {
          newData.format = `${prefix}${fy}/000001`;
        }
      }
      
      return newData;
    });
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await dispatch(createInvoiceNumberMaster(formData));
        setSnackbar({ open: true, message: 'Invoice number master created successfully!', severity: 'success' });
      } else if (dialogMode === 'edit') {
        await dispatch(updateInvoiceNumberMaster(selectedMaster.id, formData));
        setSnackbar({ open: true, message: 'Invoice number master updated successfully!', severity: 'success' });
      }
      
      handleCloseDialog();
      dispatch(fetchInvoiceNumberMasters({ page: page + 1, limit: rowsPerPage }));
      dispatch(fetchCurrentFYInvoiceNumberMasters());
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    }
  };

  // Handle delete
  const handleDelete = async (master) => {
    if (window.confirm(`Are you sure you want to delete the invoice number master for ${master.category}?`)) {
      try {
        await dispatch(deleteInvoiceNumberMaster(master.id));
        setSnackbar({ open: true, message: 'Invoice number master deleted successfully!', severity: 'success' });
        dispatch(fetchInvoiceNumberMasters({ page: page + 1, limit: rowsPerPage }));
        dispatch(fetchCurrentFYInvoiceNumberMasters());
      } catch (error) {
        setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
      }
    }
  };

  // Handle generate next invoice number
  const handleGenerateNext = async (category) => {
    try {
      const result = await dispatch(generateNextInvoiceNumber(category));
      setSnackbar({ 
        open: true, 
        message: `Next invoice number: ${result.data.invoice_number}`, 
        severity: 'success' 
      });
      dispatch(fetchCurrentFYInvoiceNumberMasters());
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(fetchInvoiceNumberMasters({ 
      page: newPage + 1, 
      limit: rowsPerPage,
      category: filterCategory || undefined
    }));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(fetchInvoiceNumberMasters({ 
      page: 1, 
      limit: newRowsPerPage,
      category: filterCategory || undefined
    }));
  };

  // Filter masters
  const filteredMasters = masters.filter(master => {
    const matchesSearch = master.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         master.financial_year.includes(searchTerm) ||
                         master.prefix.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || master.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Get category color
  const getCategoryColor = (category) => {
    switch (category.toUpperCase()) {
      case 'GOLD': return 'warning';
      case 'SILVER': return 'info';
      case 'DIAMOND': return 'secondary';
      default: return 'primary';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            <Receipt sx={{ mr: 2, verticalAlign: 'middle' }} />
            Invoice Number Master
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage invoice number sequences by category and financial year
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog('create')}
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
            }
          }}
        >
          Add New Master
        </Button>
      </Box>

      {/* Current Financial Year Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday color="primary" />
            Current Financial Year ({currentFinancialYear})
          </Typography>
          <Grid container spacing={2}>
            {currentFYMaster.map((master) => (
              <Grid item xs={12} sm={6} md={3} key={master.id}>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Chip 
                    label={master.category} 
                    color={getCategoryColor(master.category)}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="h6" color="primary">
                    {master.prefix}{master.financial_year}/000001
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current: {master.current_sequence}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleGenerateNext(master.category)}
                      disabled={generateLoading}
                      startIcon={generateLoading ? <CircularProgress size={16} /> : <Numbers />}
                    >
                      Generate Next
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="GOLD">Gold</MenuItem>
                  <MenuItem value="SILVER">Silver</MenuItem>
                  <MenuItem value="DIAMOND">Diamond</MenuItem>
                  <MenuItem value="GENERAL">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('');
                  dispatch(fetchInvoiceNumberMasters({ page: 1, limit: rowsPerPage }));
                }}
                fullWidth
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Masters Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Financial Year</TableCell>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Current Sequence</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mastersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredMasters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No invoice number masters found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMasters.map((master) => (
                    <TableRow key={master.id}>
                      <TableCell>
                        <Chip 
                          label={master.category} 
                          color={getCategoryColor(master.category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{master.financial_year}</TableCell>
                      <TableCell>{master.prefix}</TableCell>
                      <TableCell>{master.current_sequence}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {master.format}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={master.is_active ? 'Active' : 'Inactive'}
                          color={master.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', master)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', master)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(master)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Invoice Number Master' : 
           dialogMode === 'edit' ? 'Edit Invoice Number Master' : 'View Invoice Number Master'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  disabled={dialogMode === 'view'}
                >
                  <MenuItem value="GOLD">Gold</MenuItem>
                  <MenuItem value="SILVER">Silver</MenuItem>
                  <MenuItem value="DIAMOND">Diamond</MenuItem>
                  <MenuItem value="GENERAL">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Financial Year *"
                value={formData.financial_year}
                onChange={(e) => handleInputChange('financial_year', e.target.value)}
                placeholder="e.g., 25-26"
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prefix *"
                value={formData.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value)}
                placeholder="e.g., G/, S/, D/"
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Category /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Sequence"
                type="number"
                value={formData.current_sequence}
                onChange={(e) => handleInputChange('current_sequence', parseInt(e.target.value) || 1)}
                disabled={dialogMode === 'view'}
                inputProps={{ min: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Numbers /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Sequence"
                type="number"
                value={formData.max_sequence}
                onChange={(e) => handleInputChange('max_sequence', parseInt(e.target.value) || 999999)}
                disabled={dialogMode === 'view'}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    disabled={dialogMode === 'view'}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Format"
                value={formData.format}
                disabled
                helperText="Auto-generated based on prefix and financial year"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Settings /></InputAdornment>
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={createLoading || updateLoading}
              startIcon={(createLoading || updateLoading) ? <CircularProgress size={20} /> : null}
            >
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceNumberMaster;
